import { Reference } from "@apollo/client";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import Chat from "@components/messaging/Chat";
import { ResourceData, WorksheetData } from "@components/messaging/Messages";
import styles from "@components/messaging/Messages.module.css";
import MessagesControls from "@components/messaging/MessagesControls";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ChatCreateDocument,
  ChatCreateMutation,
  ChatCreateMutationVariables,
  ChatFragment,
  ChatFragmentDoc,
  GetJourneyForMessagesQuery
} from "@typing/Generated";

type Props = {
  isFab?: boolean;
  journey: GetJourneyForMessagesQuery["journey"];
  resourceData: ResourceData | undefined;
  setResourceData: (resourceData: ResourceData | undefined) => void;
  setWorksheetData: (worksheetData: WorksheetData | undefined) => void;
  worksheetData: WorksheetData | undefined;
};

type MessageSearch = {
  chatId: string | null;
  found: boolean | null;
  messageId: string | null;
  searchTerm: string | null;
};

const MessagesSmall = ({
  isFab = false,
  journey,
  resourceData,
  setResourceData,
  setWorksheetData,
  worksheetData
}: Props) => {
  const myClient = useMyClient();

  const [editingChat, setEditingChat] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const history = useHistory();
  const hideFab = isFab && (queryParams.has("hideFab") || history.location.pathname.includes("/messages"));

  const opener = useGlobalOpener(OpenerTypeEnum.MESSAGES);
  const expandedOpener = useOpener();

  const [messageSearch, setMessageSearch] = useState<MessageSearch>({
    chatId: null,
    found: null,
    messageId: null,
    searchTerm: null
  });

  const [query, setQuery] = useQueryParams({
    selectedChatId: withDefault({ ...StringParam, urlName: "messagesSelectedChat" }, null)
  });

  const handleSelectChat = (chat: ChatFragment | undefined) => {
    setQuery({ selectedChatId: chat?.id });
  };

  const selectedChat = journey.chats.find(chat => chat.id === query.selectedChatId);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (selectedChat && !isFab) {
      queryParams.set("hideMenu", "true");
      history.replace({
        hash: location.hash,
        pathname: location.pathname,
        search: queryParams.toString()
      });
    } else if (!isFab) {
      queryParams.delete("hideMenu");
      history.replace({
        hash: location.hash,
        pathname: location.pathname,
        search: queryParams.toString()
      });
    }
  }, [selectedChat, history, isFab]);

  const handleMessageSearchFind = (chatId: string, messageId: string, searchTerm: string) => {
    handleSelectChat(journey.chats.find(chat => chat.id === chatId));

    setMessageSearch({ chatId, found: false, messageId, searchTerm });
  };

  const [createChat] = useMutationWithValidation<ChatCreateMutation, ChatCreateMutationVariables>(
    ChatCreateDocument,
    "chat",
    {
      update: (store, { data }) => {
        if (data?.chatCreate?.chat) {
          store.modify({
            fields: {
              chats(cachedChatRefs: readonly Reference[]) {
                const newChatRef = store.writeFragment({
                  data: data.chatCreate?.chat,
                  fragment: ChatFragmentDoc,
                  fragmentName: "Chat"
                });
                if (newChatRef && data.chatCreate?.chat) {
                  return [...cachedChatRefs, newChatRef];
                }
                return [...cachedChatRefs];
              }
            },
            id: store.identify({ ...journey })
          });
        }
      }
    }
  );

  const onChatClientsChange = (clientIds: string[]) => {
    const clientIdsWithMe = myClient ? [...clientIds, myClient.id] : clientIds;

    const existingChat = journey.chats.find(
      chat =>
        chat.clients.length === clientIdsWithMe.length &&
        chat.clients.map(client => client.id).every(clientId => clientIdsWithMe.includes(clientId))
    );

    if (existingChat) {
      handleSelectChat(existingChat);
      return;
    }
    if (clientIds.length > 0) {
      createChat({
        clientIds: clientIdsWithMe,
        journeyId: journey.id
      }).then(res => {
        if (res?.chatCreate?.chat) {
          handleSelectChat(res.chatCreate.chat);
        }
      });
    }
  };

  useEffect(() => {
    if (expandedOpener.isOpen && !selectedChat) {
      expandedOpener.close();
    }
  }, [expandedOpener, selectedChat]);

  if (hideFab) {
    return null;
  }

  return (
    <div
      className={`${isFab ? styles.messagesFab : ""} ${opener.isOpen && isFab ? styles.fabAbsoluteOpen : ""}`}
      data-test="message-fab"
    >
      {(opener.isOpen || !isFab) && (
        <div className={isFab ? (expandedOpener.isOpen ? styles.messagesExpandedFabOpen : styles.messagesFabOpen) : ""}>
          {selectedChat ? (
            <Chat
              chat={selectedChat}
              clearChat={() => handleSelectChat(undefined)}
              editingChat={editingChat}
              expandedOpener={expandedOpener}
              isFab={isFab}
              journey={journey}
              messageSearch={messageSearch}
              onChatClientsChange={onChatClientsChange}
              resourceData={resourceData}
              setMessageSearch={setMessageSearch}
              setResourceData={setResourceData}
              setWorksheetData={setWorksheetData}
              worksheetData={worksheetData}
            />
          ) : (
            <MessagesControls
              handleMessageSearchFind={handleMessageSearchFind}
              isFab={isFab}
              journey={journey}
              messageSearch={messageSearch}
              selectedChat={selectedChat}
              setEditingChat={setEditingChat}
              setMessageSearch={setMessageSearch}
              setSelectedChat={handleSelectChat}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MessagesSmall;
